import ReactGA from 'react-ga4';
import type { Metric } from 'web-vitals';

/**
 * Track an exception/error to Google Analytics.
 * Uses the GA4 exception event type for proper categorization in GA reports.
 *
 * @param description - A description of the error (typically error.message)
 * @param fatal - Whether the error was fatal (crashed the app). Defaults to true.
 */
export const trackException = (description: string, fatal = true): void => {
  if (!ReactGA.isInitialized) {
    return;
  }

  ReactGA.event('exception', {
    description: description.substring(0, 150), // GA4 recommends max 150 chars for description
    fatal,
  });
};

/**
 * Track a custom event to Google Analytics.
 *
 * @param category - Event category (e.g., 'User', 'Error')
 * @param action - Event action (e.g., 'click', 'error_boundary_crash')
 * @param label - Optional event label for additional context
 * @param value - Optional numeric value associated with the event
 */
export const trackEvent = (category: string, action: string, label?: string, value?: number): void => {
  if (!ReactGA.isInitialized) {
    return;
  }

  ReactGA.event({
    category,
    action,
    ...(label && { label }),
    ...(value !== undefined && { value }),
  });
};

/**
 * Track an Error Boundary crash to Google Analytics.
 * This sends both an exception event and a custom event for better tracking.
 *
 * @param error - The error that was caught
 */
export const trackErrorBoundaryCrash = (error: Error | { message?: string }): void => {
  const errorMessage = error instanceof Error ? error.message : (error?.message ?? 'Unknown error');

  // Track as GA4 exception (appears in GA4 > Engagement > Events > exception)
  trackException(errorMessage, true);

  // Also track as custom event for easier filtering and correlation
  trackEvent(
    'Error',
    'error_boundary_crash',
    errorMessage.substring(0, 100), // Truncate label for readability
    1
  );
};

/**
 * Track a Web Vital metric to Google Analytics.
 * Sends Core Web Vitals (LCP, FID, CLS) and other metrics (FCP, TTFB) as custom events.
 *
 * @param metric - The web-vitals Metric object containing name, value, id, etc.
 */
export const trackWebVital = (metric: Metric): void => {
  if (!ReactGA.isInitialized) {
    return;
  }

  // Send as a custom event with the metric name as the action
  // Using 'Web Vitals' as category for easy filtering in GA4
  ReactGA.event({
    category: 'Web Vitals',
    action: metric.name,
    // Google Analytics requires an integer for value, round appropriately
    // CLS is typically small (0.1), multiply by 1000 to preserve precision
    // Other metrics (LCP, FID, FCP, TTFB) are in milliseconds, round to nearest integer
    value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
    // Include metric ID for deduplication in GA
    label: metric.id,
    // Use non-interaction to avoid affecting bounce rate
    nonInteraction: true,
  });
};
